import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { connect } from "react-redux";
import { DARK_MODE, JLR_PLANT } from "../../constants";

// Enhanced JLR-themed loading spinner
const JlrSpinnerContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'themeMode' && prop !== 'isJlr',
})(({ theme, themeMode, isJlr }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: isJlr ? '32px' : '24px',
  borderRadius: isJlr ? '20px' : '12px',
  background: isJlr 
    ? (themeMode === DARK_MODE 
      ? 'rgba(26, 45, 26, 0.2)'
      : 'rgba(248, 249, 250, 0.8)')
    : 'transparent',
  backdropFilter: isJlr ? 'blur(10px)' : 'none',
  border: isJlr 
    ? `1px solid ${themeMode === DARK_MODE ? 'rgba(255, 255, 255, 0.1)' : 'rgba(45, 74, 45, 0.1)'}`
    : 'none',
  minHeight: '120px',
  position: 'relative',
}));

// Custom spinner with JLR colors
const JlrCircularProgress = styled(CircularProgress, {
  shouldForwardProp: (prop) => prop !== 'themeMode' && prop !== 'isJlr',
})(({ theme, themeMode, isJlr }) => ({
  color: isJlr 
    ? (themeMode === DARK_MODE ? '#82C1A7' : '#2d4a2d')
    : '#f5a95f',
  '& .MuiCircularProgress-circle': {
    strokeLinecap: 'round',
  },
}));

// Loading text with proper theming
const LoadingText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'themeMode' && prop !== 'isJlr',
})(({ theme, themeMode, isJlr }) => ({
  marginTop: '16px',
  color: themeMode === DARK_MODE 
    ? 'rgba(255, 255, 255, 0.8)' 
    : 'rgba(0, 0, 0, 0.7)',
  fontFamily: isJlr ? "'Inter', sans-serif" : theme.typography.fontFamily,
  fontWeight: isJlr ? 600 : 500,
  fontSize: '14px',
  letterSpacing: isJlr ? '0.5px' : 'normal',
}));

const LoadingSpinner = ({ 
  themeMode, 
  user, 
  size = 40, 
  text = "Loading...", 
  variant = "default" // "default", "minimal", "overlay"
}) => {
  const isJlr = user?.plant === JLR_PLANT;

  if (variant === "minimal") {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={2}>
        <JlrCircularProgress 
          size={size} 
          themeMode={themeMode} 
          isJlr={isJlr}
        />
      </Box>
    );
  }

  if (variant === "overlay") {
    return (
      <Box
        position="fixed"
        top={0}
        left={0}
        width="100vw"
        height="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor="rgba(0, 0, 0, 0.5)"
        zIndex={9999}
      >
        <JlrSpinnerContainer themeMode={themeMode} isJlr={isJlr}>
          <JlrCircularProgress 
            size={size} 
            themeMode={themeMode} 
            isJlr={isJlr}
          />
          <LoadingText themeMode={themeMode} isJlr={isJlr}>
            {text}
          </LoadingText>
        </JlrSpinnerContainer>
      </Box>
    );
  }

  return (
    <JlrSpinnerContainer themeMode={themeMode} isJlr={isJlr}>
      <JlrCircularProgress 
        size={size} 
        themeMode={themeMode} 
        isJlr={isJlr}
      />
      <LoadingText themeMode={themeMode} isJlr={isJlr}>
        {text}
      </LoadingText>
    </JlrSpinnerContainer>
  );
};

const mapStateToProps = (state) => ({
  themeMode: state.theme.themeMode,
  user: state.user,
});

export default connect(mapStateToProps)(LoadingSpinner);
